import React, { useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import { motion } from 'framer-motion';
import { Download, Printer, QrCode, RefreshCw, ShieldCheck } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ACTIVE_QR_NAME,
  buildQrPayload,
  generateQrToken,
  getQrImageUrl
} from '@/lib/rankingMBV';
import { isNativePlatform, openBlobFileOnNative } from '@/utils/nativeAndroidFiles';

async function loadImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error('Falha ao carregar imagem do QR Code.'));
    image.src = url;
  });
}

async function buildQrPdfBlob(activeQr) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;
  const qrDataUrl = await loadImageAsDataUrl(getQrImageUrl(activeQr.token, 900));

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(30);
  doc.text('Ranking MBV', centerX, 96, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  doc.text('QR Code fixo para check-in das equipes', centerX, 126, { align: 'center' });

  const qrSize = 360;
  const qrX = centerX - qrSize / 2;
  doc.setDrawColor(17, 24, 39);
  doc.setLineWidth(2);
  doc.roundedRect(qrX - 18, 162, qrSize + 36, qrSize + 36, 12, 12);
  doc.addImage(qrDataUrl, 'PNG', qrX, 180, qrSize, qrSize);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(`Versão ${activeQr.version || 1}`, centerX, 590, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(75, 85, 99);
  doc.text('Use o app MBV em Perfil > Ranking MBV > Fazer Check-in.', centerX, 612, { align: 'center' });

  return doc.output('blob');
}

async function openPdfFallback(activeQr) {
  const filename = `qr-code-ranking-mbv-v${activeQr.version || 1}.pdf`;
  const blob = await buildQrPdfBlob(activeQr);

  if (isNativePlatform()) {
    await openBlobFileOnNative(blob, filename, 'application/pdf', {
      title: 'QR Code Ranking MBV',
      text: 'QR Code fixo para check-in das equipes.',
      preferShare: true,
    });
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function AdminQRCodeMBV() {
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: qrCodes = [], isLoading } = useQuery({
    queryKey: ['rankingMBVQrCodes'],
    queryFn: () => base44.entities.RankingMBVQrCode.list('-created_date', 2000)
  });

  const activeQr = useMemo(
    () => qrCodes.find((qr) => qr.name === ACTIVE_QR_NAME && qr.status === 'active') || null,
    [qrCodes]
  );

  const createQrMutation = useMutation({
    mutationFn: async () => {
      const activeCodes = qrCodes.filter((qr) => qr.name === ACTIVE_QR_NAME && qr.status === 'active');
      await Promise.all(activeCodes.map((qr) => base44.entities.RankingMBVQrCode.update(qr.id, {
        status: 'invalidated',
        invalidated_at: new Date().toISOString(),
        invalidated_by: user?.email || ''
      })));

      const nextVersion = Math.max(0, ...qrCodes.map((qr) => Number(qr.version || 0))) + 1;
      const created = await base44.entities.RankingMBVQrCode.create({
        name: ACTIVE_QR_NAME,
        token: generateQrToken(),
        version: nextVersion,
        status: 'active',
        created_by: user?.email || '',
        activated_at: new Date().toISOString()
      });

      await base44.entities.RankingMBVAdminLog.create({
        action: 'qr_regenerated',
        actor_email: user?.email || '',
        qr_code_id: created.id,
        version: nextVersion,
        created_at: new Date().toISOString()
      }).catch(() => null);

      return created;
    },
    onSuccess: () => {
      toast.success('QR Code MBV atualizado com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['rankingMBVQrCodes'] });
      queryClient.invalidateQueries({ queryKey: ['rankingMBVData'] });
    },
    onError: (error) => {
      const message = String(error?.message || '');
      if (message.includes('schema cache') || message.includes('ranking_mbv_qr_codes')) {
        toast.error('As tabelas do Ranking MBV ainda não foram aplicadas no Supabase. Rode a migration e tente novamente.');
        return;
      }
      toast.error(message || 'Não foi possível gerar o QR Code.');
    }
  });

  const downloadQr = async () => {
    if (!activeQr?.token) return;
    try {
      await openPdfFallback(activeQr);
      toast.success('PDF do QR Code preparado.');
    } catch (error) {
      toast.error(error?.message || 'Não foi possível preparar o QR Code.');
    }
  };

  const printQr = () => {
    if (!activeQr?.token) return;
    const imageUrl = getQrImageUrl(activeQr.token, 900);

    if (isNativePlatform()) {
      openPdfFallback(activeQr)
        .then(() => toast.success('Abra o PDF no seletor do celular para imprimir ou compartilhar.'))
        .catch((error) => toast.error(error?.message || 'Não foi possível abrir o PDF do QR Code.'));
      return;
    }

    if (typeof window === 'undefined' || typeof window.print !== 'function') {
      openPdfFallback(activeQr)
        .then(() => toast.success('PDF do QR Code preparado.'))
        .catch((error) => toast.error(error?.message || 'Não foi possível preparar o PDF do QR Code.'));
      return;
    }

    const image = new Image();
    image.onload = () => window.setTimeout(() => {
      try {
        window.print();
      } catch (error) {
        openPdfFallback(activeQr)
          .then(() => toast.success('PDF do QR Code preparado.'))
          .catch((fallbackError) => toast.error(fallbackError?.message || error?.message || 'Não foi possível imprimir o QR Code.'));
      }
    }, 150);
    image.onerror = () => {
      openPdfFallback(activeQr)
        .then(() => toast.success('PDF do QR Code preparado.'))
        .catch((error) => toast.error(error?.message || 'Não foi possível carregar a imagem do QR Code para impressão.'));
    };
    image.src = imageUrl;
  };

  if (isLoading) {
    return <div className="min-h-screen bg-gray-50 p-4"><LoadingSpinner /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 overflow-x-hidden">
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          .ranking-mbv-print-area,
          .ranking-mbv-print-area * {
            visibility: visible !important;
          }
          .ranking-mbv-print-area {
            display: flex !important;
            position: fixed !important;
            inset: 0 !important;
            width: 100% !important;
            min-height: 100vh !important;
            background: white !important;
            color: #111827 !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 24mm !important;
            box-sizing: border-box !important;
            z-index: 99999 !important;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>
      {activeQr?.token && (
        <div className="ranking-mbv-print-area hidden">
          <div className="text-center font-sans">
            <h1 className="text-4xl font-bold text-gray-950">Ranking MBV</h1>
            <p className="mt-3 text-lg text-gray-700">QR Code fixo para check-in das equipes</p>
            <div className="mx-auto mt-8 w-[430px] max-w-full rounded-2xl border-2 border-gray-900 p-5 bg-white">
              <img
                src={getQrImageUrl(activeQr.token, 900)}
                alt="QR Code Ranking MBV para impressão"
                className="w-full h-auto"
              />
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900">Versão {activeQr.version || 1}</p>
            <p className="mt-2 text-sm text-gray-500">Use o app MBV em Perfil &gt; Ranking MBV &gt; Fazer Check-in.</p>
          </div>
        </div>
      )}
      <div className="bg-gradient-to-br from-slate-900 to-indigo-900 text-white pt-5 sm:pt-8 pb-6 sm:pb-8 px-3 sm:px-4">
        <div className="max-w-5xl mx-auto">
          <PageHeader title="QR Code MBV" subtitle="Código fixo e permanente para check-in" showBack={true} />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 grid lg:grid-cols-[360px_1fr] gap-4 sm:gap-5">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="font-bold text-gray-900 text-base sm:text-lg">QR atual</h2>
            <Badge className={activeQr ? 'bg-emerald-600' : 'bg-amber-600'}>{activeQr ? 'Ativo' : 'Pendente'}</Badge>
          </div>

          {activeQr ? (
            <div className="text-center">
              <div className="mx-auto w-full max-w-[300px] rounded-lg border border-gray-200 bg-white p-2.5 sm:p-3">
                <img
                  src={getQrImageUrl(activeQr.token)}
                  alt="QR Code Ranking MBV"
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-gray-500 mt-3">Versão {activeQr.version || 1}</p>
              <p className="text-[11px] sm:text-xs text-gray-400 break-all mt-1 max-h-14 overflow-y-auto px-1">{buildQrPayload(activeQr.token)}</p>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed p-6 sm:p-8 text-center">
              <QrCode className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">Nenhum QR Code ativo.</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5">
            <Button variant="outline" onClick={downloadQr} disabled={!activeQr} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" onClick={printQr} disabled={!activeQr} className="w-full">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </div>
          <Button
            className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700"
            onClick={() => createQrMutation.mutate()}
            disabled={createQrMutation.isPending}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {activeQr ? 'Regenerar QR Code' : 'Gerar QR Code'}
          </Button>
        </motion.div>

        <div className="space-y-5">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 sm:p-5">
            <h2 className="font-bold text-gray-900 flex items-center gap-2 text-base sm:text-lg">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Regras de segurança
            </h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {[
                'O QR padrão permanece ativo continuamente.',
                'Um novo QR substitui e invalida o anterior.',
                'Check-in é único por membro e escala.',
                'Histórico de versões fica preservado para auditoria.'
              ].map((item) => (
                <div key={item} className="rounded-lg bg-slate-50 p-3 text-sm text-gray-700 leading-relaxed">{item}</div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 sm:p-5 border-b">
              <h2 className="font-bold text-gray-900 text-base sm:text-lg">Histórico de versões</h2>
            </div>
            {qrCodes.map((qr) => (
              <div key={qr.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 p-4 border-b last:border-b-0">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">Versão {qr.version || 1}</p>
                  <p className="text-xs text-gray-500 break-words sm:truncate">Criado por {qr.created_by || 'admin'} · {String(qr.created_date || '').slice(0, 16).replace('T', ' ')}</p>
                </div>
                <Badge className={`${qr.status === 'active' ? 'bg-emerald-600' : 'bg-slate-600'} w-fit`}>
                  {qr.status === 'active' ? 'Ativo' : 'Invalidado'}
                </Badge>
              </div>
            ))}
            {qrCodes.length === 0 && <div className="p-6 text-center text-gray-500">Nenhuma versão gerada ainda.</div>}
          </div>
        </div>
      </main>
    </div>
  );
}
